import { useEffect, useRef } from "react";
import { Search } from "lucide-react";


export interface PersonaResearchWindowSource {
  title: string;
  score: number;
  url: string;
}

interface Props {
  queries: string[];
  sources: PersonaResearchWindowSource[];
}

/**
 * Petite fenêtre auto-scroll affichée sous la première bulle d'un cadre.
 * Affiche, en cascade, les requêtes puis les sources qui ont nourri son argument.
 * Si aucune requête et aucune source, ne rend rien.
 */
export function PersonaResearchWindow({ queries, sources }: Props) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const totalRows = queries.length + sources.length;

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(id);
  }, [totalRows]);

  if (totalRows === 0) return null;

  return (
    <aside
      className="mt-2 w-full max-w-md rounded-sm border text-[12px] leading-snug"
      style={{
        background: "#0a0908",
        borderColor: "rgba(255,23,68,.45)",
        fontFamily: '"DM Mono", "JetBrains Mono", monospace',
      }}
      aria-label="Ce qui a nourri cet argument"
    >
      <header
        className="flex items-center gap-2 px-3 py-1.5 border-b"
        style={{
          color: "#F5EFE3",
          borderColor: "rgba(255,23,68,.25)",
          fontSize: 10,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
        }}
      >
        <Search size={12} strokeWidth={1.5} style={{ color: "#F5EFE3" }} />
        <span>ce qui a nourri cet argument</span>
      </header>

      <div
        ref={viewportRef}
        className="h-[90px] overflow-y-auto px-3 py-1.5"
      >
        <ul className="flex flex-col gap-1">
          {queries.map((q, i) => (
            <li
              key={`q-${i}`}
              className="qv3-prw-row"
              style={{ color: "#b0a894", animationDelay: `${i * 70}ms` }}
            >
              <span style={{ color: "#F5EFE3" }}>cherche&nbsp;: </span>
              {q}
            </li>
          ))}
          {sources.map((s, i) => (
            <li
              key={`s-${i}`}
              className="qv3-prw-row flex items-baseline gap-2"
              style={{ animationDelay: `${(queries.length + i) * 70}ms` }}
            >
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate flex-1 hover:underline"
                style={{ color: "#e0dac9" }}
                title={s.title}
              >
                {s.title || s.url}
              </a>
              <span
                style={{
                  color: "#F5EFE3",
                  fontVariantNumeric: "tabular-nums",
                  fontSize: 11,
                }}
              >
                {s.score.toFixed(2)}
              </span>
            </li>
          ))}
        </ul>
      </div>


      <style>{`
        @keyframes qv3PrwRowIn {
          from { opacity: 0; transform: translateY(-3px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .qv3-prw-row {
          opacity: 0;
          animation: qv3PrwRowIn 320ms ease-out forwards;
        }
      `}</style>
    </aside>
  );
}

export default PersonaResearchWindow;
